/**
 * Sign in.
 *
 * No tenant field, and there will not be one. The tenant is resolved server-side
 * from the request; asking for it here would let anyone probe which operators an
 * email address holds an account with, without ever completing a login.
 */

import { useState, type FormEvent } from 'react';

import { login, ApiError } from './api.ts';

export function Login({ onSignedIn }: { onSignedIn: () => void }): React.JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    try {
      await login(email, password);
      onSignedIn();
    } catch (error) {
      /*
       * One message for every failure, matching the API. Saying "no such user"
       * here would undo the server's care in making a wrong password and an
       * unknown address indistinguishable.
       */
      setMessage(
        error instanceof ApiError && error.status === 429
          ? 'Too many attempts. Try again shortly.'
          : 'Email or password is incorrect.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login-page">
      <div className="login">
        <div className="login__head">
          <div className="brand" style={{ justifyContent: 'center', fontSize: 'var(--text-lg)' }}>
            <span className="brand__mark">JE</span>
            JE Protect
          </div>
          <p className="login__sub">Sign in to manage your devices</p>
        </div>

        <form className="form card" style={{ padding: 'var(--s5)' }} onSubmit={(e) => void submit(e)}>
          <div className="field">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="field">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          <button type="submit" className="btn btn--primary btn--block" disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>

          {message ? (
            <p className="notice notice--error" role="alert">
              {message}
            </p>
          ) : null}
        </form>
      </div>
    </div>
  );
}
